import { useState } from 'react';

import { SIREN_MESSAGE, SIREN_REGEX } from '@/lib/validation/fields';
import { Building2, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface SirenCardProps {
  /** SIREN actuel (null si jamais renseigné) */
  siren: string | null;
  /** Persiste le SIREN normalisé (9 chiffres) — doit invalider les queries côté appelant */
  onSave: (siren: string) => Promise<unknown>;
  /** Ce que le SIREN débloque, affiché dans l'aide (ex. « connecter votre compte Stripe ») */
  requiredFor: string;
  accentColor: 'blue' | 'emerald';
}

const ACCENT = {
  blue: { icon: 'bg-blue-100 text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
  emerald: { icon: 'bg-emerald-100 text-emerald-600', button: 'bg-emerald-600 hover:bg-emerald-700' },
};

/**
 * Saisie du SIREN (Organization ou Club) — décision D6 : optionnel à la
 * création, requis au 1er paiement (Stripe Connect orga / abonnement Club Pro).
 */
export function SirenCard({ siren, onSave, requiredFor, accentColor }: SirenCardProps) {
  const [value, setValue] = useState(siren ?? '');
  const [saving, setSaving] = useState(false);

  const cleaned = value.replace(/\s+/g, '');
  const isValid = SIREN_REGEX.test(cleaned);
  const showError = cleaned !== '' && !isValid;
  const isDirty = cleaned !== (siren ?? '');
  const colors = ACCENT[accentColor];

  async function handleSave() {
    if (!isValid || !isDirty) return;
    setSaving(true);
    try {
      await onSave(cleaned);
      toast.success('SIREN enregistré');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erreur lors de l'enregistrement du SIREN");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
      <div className='flex items-center gap-3 mb-4'>
        <div className={`w-10 h-10 ${colors.icon} rounded-lg flex items-center justify-center`}>
          <Building2 className='w-5 h-5' />
        </div>
        <div className='min-w-0'>
          <h3 className='font-bold text-slate-900'>Numéro SIREN</h3>
          <p className='text-sm text-slate-500'>Identification de votre structure (9 chiffres)</p>
        </div>
        {siren && (
          <span className='ml-auto inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700'>
            <Check className='w-3.5 h-3.5' />
            Renseigné
          </span>
        )}
      </div>

      <p className='text-sm text-slate-600 mb-4'>
        Le SIREN est obligatoire pour {requiredFor} (facturation et obligations déclaratives). Vous le trouverez sur
        l&apos;avis de situation INSEE de votre structure.
      </p>

      <div className='flex flex-col sm:flex-row sm:items-start gap-3 max-w-md'>
        <div className='flex-1 min-w-0'>
          <input
            type='text'
            inputMode='numeric'
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder='123 456 789'
            maxLength={11}
            aria-label='Numéro SIREN'
            aria-invalid={showError}
            className={`w-full px-4 py-2.5 border rounded-xl bg-slate-50 text-sm font-mono tracking-wider text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:border-transparent ${
              showError ? 'border-red-300 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-500'
            }`}
          />
          {showError && <p className='mt-1.5 text-xs font-semibold text-red-600'>{SIREN_MESSAGE}</p>}
        </div>
        <button
          type='button'
          onClick={() => void handleSave()}
          disabled={!isValid || !isDirty || saving}
          className={`cursor-pointer inline-flex items-center justify-center gap-2 px-5 py-2.5 ${colors.button} text-white text-sm font-semibold rounded-xl transition disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {saving ? <Loader2 className='w-4 h-4 animate-spin' /> : <Check className='w-4 h-4' />}
          Enregistrer
        </button>
      </div>
    </div>
  );
}
